/**
 * Cap JsSIP REGISTER retries after `registrationFailed`.
 *
 * Leftover Visual IVR / live-mode UAs with `register: true` plus
 * `connection_recovery` (2–30s) will otherwise REGISTER-loop all day
 * against a missing Asterisk endpoint (`No matching endpoint found` /
 * `Failed to authenticate`). Production voice logs on 24 Aug 2026
 * showed leftover `vivrtesttool-avatar-*` identities doing exactly that.
 *
 * This helper is the policy only — `initializeVoiceCall` owns the UA /
 * socket teardown. A fresh UA (new Listen / new page) gets a fresh cap.
 * Successful `registered` resets the consecutive-failure count so a
 * brief network blip does not burn the budget forever.
 */

export const SIP_REGISTRATION_MAX_FAILURES = 5
export const SIP_REGISTRATION_RETRY_DELAY_MS = 5_000

export type SipRegistrationRetryDecision =
  | {
      kind: 'retry'
      failureCount: number
      maxFailures: number
      delayMs: number
    }
  | {
      kind: 'give-up'
      failureCount: number
      maxFailures: number
    }

export interface SipRegistrationRetryCap {
  readonly consecutiveFailures: number
  readonly givenUp: boolean
  recordRegistered: () => void
  recordRegistrationFailed: () => SipRegistrationRetryDecision
  shouldRecoverConnection: () => boolean
}

export function createSipRegistrationRetryCap(options?: {
  maxFailures?: number
  retryDelayMs?: number
}): SipRegistrationRetryCap {
  const maxFailures = options?.maxFailures ?? SIP_REGISTRATION_MAX_FAILURES
  const retryDelayMs = options?.retryDelayMs ?? SIP_REGISTRATION_RETRY_DELAY_MS
  let consecutiveFailures = 0
  let givenUp = false

  return {
    get consecutiveFailures() {
      return consecutiveFailures
    },
    get givenUp() {
      return givenUp
    },
    recordRegistered() {
      if (givenUp) {
        return
      }
      consecutiveFailures = 0
    },
    recordRegistrationFailed(): SipRegistrationRetryDecision {
      if (givenUp) {
        return { kind: 'give-up', failureCount: consecutiveFailures, maxFailures }
      }

      consecutiveFailures += 1
      if (consecutiveFailures >= maxFailures) {
        givenUp = true
        return { kind: 'give-up', failureCount: consecutiveFailures, maxFailures }
      }

      return {
        kind: 'retry',
        failureCount: consecutiveFailures,
        maxFailures,
        delayMs: retryDelayMs
      }
    },
    shouldRecoverConnection() {
      return !givenUp
    }
  }
}
